// Project API functions for Live Workspace
import { apiClient } from './client'
import type {
  ProjectDetail,
  ProjectListResponse,
  ActivityListResponse,
  ExchangeListResponse,
} from '../types/project-graph'

export const projectAPI = {
  // List all user projects
  listProjects: (params?: { page?: number; limit?: number; database?: string }) =>
    apiClient.get<ProjectListResponse>('/projects', { params }),

  // Get single project details
  getProject: (
    projectId: string,
    options?: {
      show_description?: boolean
      show_notes?: boolean
    },
  ) => apiClient.get<ProjectDetail>(`/projects/${projectId}`, { params: options }),

  // List activities in a project
  listActivities: (params?: {
    project_id?: string
    page?: number
    limit?: number
    show_exchanges?: boolean
  }) => apiClient.get<ActivityListResponse>('/activities', { params }),

  // Get activity with exchanges
  getActivity: (activityId: string, showExchanges: boolean = true) =>
    apiClient.get(`/activities/${activityId}`, {
      params: { show_exchanges: showExchanges },
    }),

  // List exchanges for an activity
  listExchanges: (activityId: string) =>
    apiClient.get<ExchangeListResponse>(`/activities/${activityId}/exchanges`),

  // Create ecoinvent-based exchange for an activity. Backend dispatches by
  // exchange_type: technosphere requires input_activity_code; biosphere
  // requires input_biosphere_code. input_database is required either way.
  createEcoinventExchange: (
    activityId: string,
    data: {
      amount: number
      exchange_type: string
      input_database: string
      input_activity_code?: string
      input_biosphere_code?: string
    },
  ) => apiClient.post(`/activities/${activityId}/exchanges/ecoinvent`, data),

  // Create a new activity in a project
  createActivity: (data: {
    project_id: string
    code: string
    name: string
    unit: string
    location?: string
    comment?: string
    reference_product?: string
  }) => apiClient.post('/activities', data),

  // Create custom exchange (link to project activity)
  createCustomExchange: (
    activityId: string,
    data: {
      name: string
      amount: number
      unit: string
      exchange_type: string
      input_activity_code?: string
      input_database?: string
    },
  ) => apiClient.post(`/activities/${activityId}/exchanges/custom`, data),

  // Update an activity (PATCH — RFC 7396 partial update; omitted fields preserved)
  updateActivity: (
    activityId: string,
    data: {
      code?: string
      name?: string
      unit?: string
      location?: string
      comment?: string
      reference_product?: string
      project_id?: string
    },
  ) => apiClient.patch(`/activities/${activityId}`, data),

  // Delete an activity
  deleteActivity: (activityId: string) => apiClient.delete(`/activities/${activityId}`),

  // Update an exchange (PATCH — partial; ecoinvent/biosphere refs reject name/unit server-side)
  updateExchange: (activityId: string, exchangeId: string, patch: ExchangePatch) =>
    apiClient.patch(`/activities/${activityId}/exchanges/${exchangeId}`, patch),

  // Delete an exchange
  deleteExchange: (activityId: string, exchangeId: string) =>
    apiClient.delete(`/activities/${activityId}/exchanges/${exchangeId}`),
}

// Partial update payload for an exchange (PATCH /activities/{id}/exchanges/{id}).
// All fields optional; null on `comment`/`uncertainty_*` clears the value.
export interface ExchangePatch {
  amount?: number
  name?: string
  unit?: string
  comment?: string | null
  uncertainty_type?: string | null
  uncertainty_params?: Record<string, unknown> | null
}
